import type { UserContextEvent } from '../telemetry-models';

/**
 * Display information including screen dimensions, resolution, and device pixel ratio
 */
export type DisplayInfo = Pick<
  UserContextEvent,
  'screen_width' | 'screen_height' | 'device_pixel_ratio' | 'orientation'
>;

/**
 * Viewport information including dimensions, scale, and iframe status
 */
export type ViewportInfo = Pick<UserContextEvent, 'viewport_width' | 'viewport_height' | 'visual_scale' | 'is_iframe'>;

/**
 * Detects display and viewport information
 * Implements the IDetector pattern using static methods for both display and viewport detection
 */
export class DisplayDetector {
  /**
   * Capture display information (screen dimensions, resolution, pixel ratio)
   */
  static captureDisplay(): DisplayInfo {
    return {
      screen_width: this.bucketScreenDimension(screen.width),
      screen_height: this.bucketScreenDimension(screen.height),
      device_pixel_ratio: window.devicePixelRatio || 1,
      orientation: this.getOrientation(),
    };
  }

  static captureViewport(): ViewportInfo {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    return {
      viewport_width: viewportWidth,
      viewport_height: viewportHeight,
      visual_scale: this.getVisualScale(),
      is_iframe: this.detectIframe(),
    };
  }

  /** Bucket to common resolutions to reduce unique values. */
  static bucketScreenDimension(value: number): number {
    const commonWidths = [320, 375, 414, 768, 1024, 1280, 1366, 1440, 1920, 2560, 3840];
    return commonWidths.reduce((prev, curr) => (Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev));
  }

  static getOrientation(): 'portrait' | 'landscape' {
    if (screen.orientation) {
      return screen.orientation.type.startsWith('portrait') ? 'portrait' : 'landscape';
    }
    return window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
  }

  static getVisualScale(): number | undefined {
    if ('visualViewport' in window && window.visualViewport) {
      return Math.round(window.visualViewport.scale * 10) / 10;
    }
    return undefined;
  }

  static detectIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  }

  static calculateViewportCategory(width: number): 'mobile' | 'tablet' | 'desktop' {
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  static isSignificantViewportChange(old: ViewportInfo | null, current: ViewportInfo, threshold: number): boolean {
    if (!old) return true;

    const widthDiff = Math.abs(old.viewport_width - current.viewport_width);
    const heightDiff = Math.abs(old.viewport_height - current.viewport_height);

    return widthDiff > threshold || heightDiff > threshold;
  }
}
